import {
  dbui_v1_NotableList,
  dbuiV1GetNotableByIdQueryKey,
  dbuiV1GetNotablesInfiniteOptions,
  dbui_v1_Notable,
} from '@antimatterhq/asl-api-ts-v1';
import {
  InfiniteData,
  useInfiniteQuery,
} from '@antimatterhq/ui/hooks/queries/useInfiniteQuery';
import { useQueryClient } from '@antimatterhq/ui/hooks/queries/useQueryClient';
import { produce } from 'immer';
import { VIRTUALIZED_TABLE_DEFAULT_PAGE_SIZE } from '@antimatterhq/ui/constants/table';
import { useWarehouseLocation } from '@/contexts/WarehouseLocationContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { defaultOptions } from '@/utils/query';

export const useGetNotablesInfinite = ({
  includeNotOpen = true,
  beforeDate = undefined,
}: {
  includeNotOpen?: boolean;
  beforeDate?: string;
}) => {
  const { workspace } = useWorkspace();
  const { selectedWarehouse } = useWarehouseLocation();
  const warehouse = selectedWarehouse?.id || '';
  const queryClient = useQueryClient();

  const options = dbuiV1GetNotablesInfiniteOptions({
    ...defaultOptions(),
    path: { workspace },
    query: {
      warehouse,
      limit: VIRTUALIZED_TABLE_DEFAULT_PAGE_SIZE,
      includeNotOpen,
      beforeDate,
    },
  });

  const queryKey = options.queryKey;

  const optimisticUpdateStateNotables = ({
    ids,
    newState,
  }: {
    ids: string[];
    newState: dbui_v1_Notable['state'];
  }) => {
    queryClient.setQueryData<InfiniteData<dbui_v1_NotableList, unknown>>(
      queryKey,
      (oldData) => {
        if (!oldData) return oldData;

        return produce(oldData, (draft) => {
          draft.pages.forEach((page) => {
            page.notables.forEach((notable) => {
              if (notable.id && ids.includes(notable.id)) {
                // eslint-disable-next-line no-param-reassign
                notable.state = newState;
              }
            });
          });
        });
      },
    );
  };

  const optimisticUpdateAssignNotables = ({
    ids,
    assignTo,
  }: {
    ids: string[];
    assignTo: string;
  }) => {
    queryClient.setQueryData<InfiniteData<dbui_v1_NotableList, unknown>>(
      queryKey,
      (oldData) => {
        if (!oldData) return oldData;

        return produce(oldData, (draft) => {
          draft.pages.forEach((page) => {
            page.notables.forEach((notable) => {
              if (notable.id && ids.includes(notable.id)) {
                // eslint-disable-next-line no-param-reassign
                notable.assignedTo = assignTo;
                // eslint-disable-next-line no-param-reassign
                notable.state = 'Assigned';
              }
            });
          });
        });
      },
    );
  };

  const optimisticUpdateNotableById = (notable: dbui_v1_Notable) => {
    queryClient.setQueryData(
      dbuiV1GetNotableByIdQueryKey({
        ...defaultOptions(),
        path: { workspace, id: notable.id },
        query: { warehouse },
      }),
      notable,
    );
  };

  const query = useInfiniteQuery({
    ...options,
    enabled: !!workspace && !!warehouse,
    getNextPageParam: (page) => page.cursor,
  });

  return {
    ...query,
    optimisticUpdateAssignNotables,
    optimisticUpdateStateNotables,
    optimisticUpdateNotableById,
  };
};
